import { useState, useEffect, useCallback, useRef, useMemo } from 'react';

export interface UsePipelineCardExpansionReturn {
  // Currently expanded card
  openCardId: number | null;
  setOpenCardId: (id: number | null) => void;

  // Highlighted card (for search results)
  highlightedCustomerId: number | null;
  setHighlightedCustomerId: (id: number | null) => void;

  // Highlighted task
  highlightedTaskId: number | null;
  setHighlightedTaskId: (id: number | null) => void;

  // Actions
  expandCard: (customerId: number) => void;
  collapseCard: () => void;
  toggleCard: (customerId: number) => void;

  // Skip auto-scroll flag (for search-triggered expansions)
  skipAutoScrollRef: React.MutableRefObject<boolean>;
}

/**
 * Hook to manage pipeline card expansion state
 * Handles which card is expanded and highlight effects
 */
export function usePipelineCardExpansion(): UsePipelineCardExpansionReturn {
  const [openCardId, setOpenCardId] = useState<number | null>(null);
  const [highlightedCustomerId, setHighlightedCustomerId] = useState<number | null>(null);
  const [highlightedTaskId, setHighlightedTaskId] = useState<number | null>(null);

  // Skip auto-scroll flag for search-triggered expansions
  const skipAutoScrollRef = useRef(false);

  // Auto-clear highlight after delay
  useEffect(() => {
    if (!highlightedCustomerId) return;
    if (typeof window === 'undefined') return;

    const timeout = window.setTimeout(() => {
      setHighlightedCustomerId(null);
    }, 1800);

    return () => window.clearTimeout(timeout);
  }, [highlightedCustomerId]);

  const expandCard = useCallback((customerId: number) => {
    setOpenCardId(customerId);
  }, []);

  const collapseCard = useCallback(() => {
    setOpenCardId(null);
  }, []);

  const toggleCard = useCallback((customerId: number) => {
    setOpenCardId((current) => (current === customerId ? null : customerId));
  }, []);

  return useMemo(
    () => ({
      openCardId,
      setOpenCardId,
      highlightedCustomerId,
      setHighlightedCustomerId,
      highlightedTaskId,
      setHighlightedTaskId,
      expandCard,
      collapseCard,
      toggleCard,
      skipAutoScrollRef,
    }),
    [
      openCardId,
      highlightedCustomerId,
      highlightedTaskId,
      expandCard,
      collapseCard,
      toggleCard,
    ]
  );
}
